import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Card,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  ToggleButtonGroup,
} from "@mui/material";
import { ToggleButton as MuiToggleButton } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import SparkMD5 from "spark-md5";
import { useConfig } from "../../contexts/ConfigContext";
import { useS3Upload } from "../../services/awsUploadService";
import { MediaItemDetailed, UploadPhase, RelatedEntityItem } from "../../types";
import { formatFileSize, isFileSizeValid } from "../../utilities/fileUtils";

import { inferFileType } from "../../utilities/uploaderUtils";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { generateUUID } from "../../utilities/uuid";
import { CMButton } from "../common/CMButton";
import ToggleButton from "../common/ToggleButton";
import PreviewDialog from "./PreviewDialog";
import SingleItemPreview from "./SingleItemPreview";
import { UploadButton } from "./UploadButton";
import { UploadProgressBars } from "./UploadProgressBars";
import UploadTableContainer from "./UploadTableContainer";
import { useSpinner } from '../../contexts/SpinnerContext';
import { useUser } from '../../contexts/UserContext';
import { transformRelationships } from "../../services/mediaEditService";
import SpreadsheetUpload from "./SpreadsheetUpload";
import { SpreadsheetRow } from "../../utilities/csvParser";

interface UploadResult {
  file: File;
  url: string;
  uploadType: string;
}

interface UploadError extends Error {
  failedFiles?: Array<{ file: string; error: string }>;
}

const MD5_SIZE_LIMIT = 50 * 1024 * 1024; // 50MB

const calculateMD5 = async (file: File): Promise<string> => {
  if (file.size > MD5_SIZE_LIMIT) {
    console.warn(
      `[MD5] Skipping hash calculation for ${file.name} (${formatFileSize(
        file.size
      )}) - file too large`
    );
    return "skipped-large-file";
  }

  const startTime = performance.now();

  return new Promise((resolve, reject) => {
    const chunkSize = 2097152; // Read in chunks of 2MB
    const chunks = Math.ceil(file.size / chunkSize);
    let currentChunk = 0;
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();

    fileReader.onload = (e) => {
      spark.append(e.target!.result as ArrayBuffer);
      currentChunk++;

      if (currentChunk < chunks) {
        loadNext();
      } else {
        const hash = spark.end();
        const endTime = performance.now();
        console.log(
          `[MD5] Hash calculation for ${file.name} (${formatFileSize(
            file.size
          )}) took ${(endTime - startTime).toFixed(2)}ms`
        );
        resolve(hash);
      }
    };

    fileReader.onerror = (e) => {
      reject(e);
    };

    function loadNext() {
      const start = currentChunk * chunkSize;
      const end =
        start + chunkSize >= file.size ? file.size : start + chunkSize;
      fileReader.readAsArrayBuffer(file.slice(start, end));
    }

    loadNext();
  });
};

const BulkUploadPage: React.FC = () => {
  const config = useConfig();
  const { hasWritePermissions } = useUser();
  const [uploadItems, setUploadItems] = useState<MediaItemDetailed[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<MediaItemDetailed | null>(
    null
  );
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(true);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [showDropZone, setShowDropZone] = useState(true);
  const [showProgressBars, setShowProgressBars] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    message: string;
    failedFiles?: Array<{ file: string; error: string }>;
  } | null>(null);
  const [uploadMode, setUploadMode] = useState<'regular' | 'spreadsheet'>('regular');
  const [spreadsheetFiles, setSpreadsheetFiles] = useState<File[]>([]);
  const [selectedBlobUrl, setSelectedBlobUrl] = useState<string>("");

  const { uploadFiles, progress: uploadProgress } = useS3Upload();

  // Threshold will be determined dynamically by backend during upload
  // This is just for UI display purposes and doesn't affect actual upload logic
  const getMultipartThreshold = () => {
    // Default threshold for UI display - actual threshold comes from backend
    return 5 * 1024 * 1024; // 5MB
  };

  const [currentPhase, setCurrentPhase] = useState<UploadPhase>("importing");

  const { startLoading, stopLoading } = useSpinner();

  useEffect(() => {
    if (selectedItemId && !isPreviewEnabled) {
      setIsPreviewEnabled(true);
    }
  }, [selectedItemId]);

  // Generate a blob URL only when the selected row changes and only for local files
  useEffect(() => {
    // Cleanup previous URL first
    if (selectedBlobUrl) {
      URL.revokeObjectURL(selectedBlobUrl);
      setSelectedBlobUrl("")
    }
    const item = uploadItems.find(i => String(i.id) === String(selectedItemId));
    if (!item) return;
    if (item.file instanceof File) {
      // Only create on selection
      const url = URL.createObjectURL(item.file);
      setSelectedBlobUrl(url);
    }
  }, [selectedItemId, uploadItems]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (selectedBlobUrl) URL.revokeObjectURL(selectedBlobUrl);
    };
  }, [selectedBlobUrl]);

  const clearUploadStates = () => {
    setUploadItems([]);
    setSelectedItemId(undefined);
    setPreviewItem(null);
    setPendingFiles([]);
  };

  const handleUploadFinishConfirmation = () => {
    clearUploadStates();
    setUploadComplete(false);
    setCurrentPhase("importing");
  };

  const handlePreviewClick = (previewData: {
    mediaUrl: string;
    mediaType?: string;
    thumbnail?: string;
  }) => {
    const item = uploadItems.find(
      (item) =>
        (item.file instanceof File
          ? URL.createObjectURL(item.file)
          : item.url) === previewData.mediaUrl
    );

    if (item) {
      setPreviewItem(item);
      setPreviewDialogOpen(true);
    }
  };

  const handleFilesDropped = async (files: File[]) => {
    // First filter out files that exceed size limit
    const validSizeFiles = files.filter((file) => {
      const isValid = isFileSizeValid(file);
      if (!isValid) {
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `File "${file.name}" exceeds maximum size limit of 4GB`,
          { variant: 'warning' }
        );
      }
      return isValid;
    });

    // Check for duplicate files based on filename
    const existingFilenames = new Set(
      uploadItems
        .filter(item => item.file instanceof File || item.original_file?.filename)
        .map(item => item.file instanceof File ? item.file.name : item.original_file?.filename)
    );
    
    // Filter out duplicate filenames
    const nonDuplicateFiles = validSizeFiles.filter(file => {
      if (existingFilenames.has(file.name)) {
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `File "${file.name}" already added to upload queue`,
          { variant: 'info' }
        );
        return false;
      }
      return true;
    });

    if (nonDuplicateFiles.length === 0) {
      return; // No valid non-duplicate files to process
    }

    // Process the remaining files
    const newItems = await Promise.all(
      nonDuplicateFiles.map(async (file) => {
        const type = inferFileType(file.name);
        
        let previewUrl;
        if (type === 'photo') {
          // Only generate preview URLs for photos (which are just the file blob)
          previewUrl = URL.createObjectURL(file);
        } else {
          previewUrl = undefined;
        }

        const hashmd5 = await calculateMD5(file);
        
        // Check for duplicate by MD5 hash (for identical content with different names)
        const duplicateByHash = uploadItems.find(item => 
          item.original_file?.hashmd5 === hashmd5 && hashmd5 !== "skipped-large-file"
        );
        
        if (duplicateByHash) {
          enqueueUniqueSnackbar(
            enqueueSnackbar,
            `File "${file.name}" has identical content to "${duplicateByHash.original_file?.filename}"`,
            { variant: 'info' }
          );
          return null; // Skip this file
        }

        const newItem = {
          id: generateUUID(),
          name: "",
          description: "",
          type,
          media_type: type,
          file,
          fileName: file.name,
          previewUrl,
          original_file: {
            filename: file.name,
            type: file.type,
            size: `${Math.round(file.size / 1024)}kb`,
            hashmd5,
          },
          relationships: {
            related_entities: {},
            geo_entities: {},
            media: [],
          },
        } as MediaItemDetailed;
        
        return newItem;
      })
    );

    // Filter out nulls from skipped files - use type guard for safer filtering
    const validNewItems: MediaItemDetailed[] = newItems.filter((item): item is MediaItemDetailed => item !== null);
    
    if (validNewItems.length > 0) {
      setUploadItems((prev) => [...prev, ...validNewItems]);
    }
  };

  const handleItemUpdate = (updates: Partial<MediaItemDetailed>) => {
    // Keep track of updates to upload items
    try {
      startLoading();
      setUploadItems((prev) => {
        const newItems = prev.map((item) => {
          if (item.id === updates.id) {
            const updatedItem = { ...item, ...updates };
            return updatedItem;
          }
          return item;
        });
        return newItems;
      });
    } catch (error) {
      enqueueSnackbar("Error updating item", { variant: "error" });
    } finally {
      stopLoading();
    }
  };

  const handleBulkUpdate = (
    updates: Array<{ id: string; updates: Partial<MediaItemDetailed> }>
  ) => {
    updates.forEach(({ id, updates: itemUpdates }) => {
      handleItemUpdate({ id, ...itemUpdates });
    });
  };

  const handleUploadAll = async () => {
    try {
      setCurrentPhase("uploading");
      setShowProgressBars(true);
      setErrorDetails(null);

      const files = uploadItems
        .map((item) => item.file)
        .filter((file): file is File => !!file);

      const metadata = files.map((file) => {
        const item = uploadItems.find((item) => item.file === file);
        if (!item) throw new Error("Item not found");

        const transformedRelationships = transformRelationships(
          item.relationships,
          config
        );
        if (!transformedRelationships) {
          throw new Error(`Invalid relationships format for file: ${file.name}`);
        }

        const meta = {
          id: item.id,
          name: item.name || "",
          description: item.description || "",
          relationships: transformedRelationships,
        };
        
        return meta;
      });
      const results = await uploadFiles(files, metadata);
      
      // Remove successfully uploaded items from the table
      setUploadItems(prev => prev.filter(item => 
        !results.some(result => result.file.name === item.file?.name)
      ));

      setCurrentPhase("complete");
    } catch (error: unknown) {
      // Keep only the failed files in the table
      const uploadError = error as UploadError;
      if (uploadError.failedFiles) {
        setUploadItems(prev => prev.filter(item => 
          uploadError.failedFiles?.some(failed => 
            failed.file === item.file?.name
          )
        ));
        
        setErrorDetails({
          message: uploadError.message,
          failedFiles: uploadError.failedFiles
        });
      }
      
      // Reset UI state
      setCurrentPhase("importing");
      setShowProgressBars(false);

      enqueueSnackbar(
        <Box>
          <Typography variant="subtitle2" color="inherit">
            Upload Failed
          </Typography>
          <Typography variant="body2">
            {uploadError instanceof Error ? uploadError.message : "Unknown error"}
          </Typography>
        </Box>,
        {
          variant: "error",
          action: (key) => (
            <IconButton size="small" color="inherit" onClick={() => closeSnackbar(key)}>
              <CloseIcon />
            </IconButton>
          ),
        }
      );
    }
  };

  const handleSpreadsheetProcessed = (matches: Array<{ file: File; row: SpreadsheetRow }>) => {
    // Convert matches to MediaItemDetailed format
    const newItems = matches.map(({ file, row }) => {
      const type = inferFileType(file.name);
      
      let previewUrl;
      if (type === 'photo') {
        previewUrl = URL.createObjectURL(file);
      }

      const newItem = {
        id: generateUUID(),
        name: row.name || "",
        description: row.description || "",
        type,
        media_type: type,
        file,
        fileName: file.name,
        previewUrl,
        original_file: {
          filename: file.name,
          type: file.type,
          size: `${Math.round(file.size / 1024)}kb`,
          hashmd5: "spreadsheet-upload", // Skip MD5 for spreadsheet uploads
        },
        relationships: {
          related_entities: {},
          geo_entities: {},
          media: [],
        },
      } as MediaItemDetailed;
      
      return newItem;
    });

    setUploadItems(newItems);
    
    // Select first item if any
    if (newItems.length > 0) {
      setSelectedItemId(String(newItems[0].id));
      setIsPreviewEnabled(true);
    }
  };

  const handleDeleteItem = async (id: string) => {
    try {
      // Clear selection if deleting selected item
      if (selectedItemId === id) {
        setSelectedItemId(undefined);
      }

      // Clear preview if showing deleted item
      if (previewItem?.id === id) {
        setPreviewItem(null);
        setPreviewDialogOpen(false);
      }

      // Allow state updates to propagate
      await new Promise(resolve => setTimeout(resolve, 50));

      setUploadItems(prevItems => {
        const newItems = prevItems.filter(item => item.id !== id);

        // Update preview state based on remaining items
        if (newItems.length === 0) {
          setIsPreviewEnabled(false);
        } else if (selectedItemId === id) {
          // Select first remaining item, ensure id is string
          setSelectedItemId(String(newItems[0].id));
        }

        return newItems;
      });
    } catch (error) {
      console.error('Error deleting item:', error);
      enqueueSnackbar('Error deleting item', { variant: 'error' });
    }
  };

  // Check permissions early - if user can't upload, show permission message
  if (!hasWritePermissions()) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Upload Access Required
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
          You need upload permissions to access this feature. Please contact your administrator if you believe you should have upload access.
        </Typography>
      </Box>
    );
  }

  const renderContent = () => {
    switch (currentPhase) {
      case "importing":
      case "uploading":
      case "complete":
        if (uploadMode === 'spreadsheet' && uploadItems.length === 0) {
          return (
            <SpreadsheetUpload
              onSpreadsheetProcessed={handleSpreadsheetProcessed}
              mediaFiles={spreadsheetFiles}
              onMediaFilesChange={setSpreadsheetFiles}
            />
          );
        }
        return (
          <UploadTableContainer
            items={uploadItems}
            onItemSelect={setSelectedItemId}
            selectedItemId={selectedItemId}
            onPreviewClick={handlePreviewClick}
            onItemUpdate={handleItemUpdate}
            onBulkUpdate={handleBulkUpdate}
            onFilesDropped={handleFilesDropped}
            onDeleteItem={handleDeleteItem}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        p: "2em",
        gap: "2em",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Upload Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={uploadMode}
          exclusive
          onChange={(event, newMode) => {
            if (newMode !== null) {
              setUploadMode(newMode);
              // Clear existing items when switching modes
              if (newMode !== uploadMode) {
                setUploadItems([]);
                setSelectedItemId(undefined);
                setSpreadsheetFiles([]);
              }
            }
          }}
          aria-label="upload mode"
        >
          <MuiToggleButton value="regular" aria-label="regular upload">
            Regular Upload
          </MuiToggleButton>
          <MuiToggleButton value="spreadsheet" aria-label="spreadsheet upload">
            Spreadsheet Upload
          </MuiToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box 
        sx={{ 
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {currentPhase === "uploading" ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <UploadProgressBars progress={uploadProgress} items={uploadItems} />
          </Box>
        ) : (
          <>
            <Collapse
              in={isPreviewEnabled && selectedItemId !== undefined}
              collapsedSize={0}
            >
              <Box sx={{ height: "30vh", position: "relative" }}>
                {selectedItemId && (
                  <SingleItemPreview
                    item={
                      uploadItems.find((item) => item.id === selectedItemId)!
                    }
                    onUpdate={handleItemUpdate}
                    onPreviewClick={(url) => {
                      if (url) {
                        setPreviewItem(
                          uploadItems.find(
                            (item) => item.id === selectedItemId
                          )!
                        );
                        setPreviewDialogOpen(true);
                      }
                    }}
                    mediaUrl={selectedBlobUrl || undefined}
                  />
                )}
              </Box>
            </Collapse>

            {selectedItemId && (
              <ToggleButton
                isOpen={isPreviewEnabled}
                onClick={() => setIsPreviewEnabled(!isPreviewEnabled)}
                direction="vertical"
                position={{
                  left: "50%",
                  transform: "translateX(-50%)",
                  top: isPreviewEnabled ? "28vh" : "-1vh",
                }}
              />
            )}

            <Box 
              sx={{ 
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Card 
                elevation={3} 
                sx={{ 
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {renderContent()}
              </Card>

              {uploadItems.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    zIndex: 2,
                  }}
                >
                  <UploadButton
                    items={uploadItems}
                    onUpload={handleUploadAll}
                    disabled={false}
                    uploadProgress={uploadProgress}
                  />
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={currentPhase === "complete"}
        onClose={() => {}}
        PaperProps={{
          sx: {
            minWidth: "auto",
            maxWidth: "400px",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            m: 0,
            p: 2,
            borderRadius: 2,
            boxShadow: 24,
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Upload Complete! 🎉</Typography>
            <Typography variant="body2" color="text.secondary">
              All files have been successfully uploaded.
            </Typography>
            <CMButton
              onClick={handleUploadFinishConfirmation}
              variant="contained"
              color="primary"
              sx={{ mt: 1 }}
            >
              OK
            </CMButton>
          </Box>
        </DialogContent>
      </Dialog>

      {previewItem && (
        <PreviewDialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          file={previewItem.file ?? null}
          fileType={previewItem.media_type ?? previewItem.type}
        />
      )}
    </Box>
  );
};

export default BulkUploadPage;
